/**
 * Menu open / close sound effects — synthesised in-browser with the Web Audio
 * API. Zero external assets, so nothing to bundle, no CORS, no autoplay
 * gotchas after the first user interaction.
 *
 * Two very short, distinct sounds:
 *   - `whoosh()`  — soft ascending sine sweep (60ms) for menu open
 *   - `click()`   — crisp descending blip (35ms) for menu close
 *
 * Volume is deliberately subtle (~-24dB); the sound is a texture, not a beep.
 */

let _ctx = null;

/** Lazily create a single shared AudioContext to avoid repeated construction. */
function getCtx() {
  if (typeof window === "undefined") return null;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!_ctx) _ctx = new AC({ latencyHint: "interactive" });
  // Autoplay policy: `resume()` no-ops after the first user gesture that
  // triggered it (the button click that opened the menu counts).
  if (_ctx.state === "suspended") _ctx.resume().catch(() => {});
  return _ctx;
}

/**
 * Envelope helper — attack/decay/release on a gain node for a clean sound
 * without clicks or pops at start/end.
 */
function envelope(ctx, gain, duration, peak = 0.06) {
  const now = ctx.currentTime;
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(peak, now + 0.006);       // attack
  gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);  // decay
}

/** Soft ascending "whoosh" — menu-open cue. */
export function playMenuOpenSound() {
  const ctx = getCtx();
  if (!ctx) return;

  // Body: sine sweeping up an octave (A3 → A4).
  const osc = ctx.createOscillator();
  osc.type = "sine";
  osc.frequency.setValueAtTime(220, ctx.currentTime);
  osc.frequency.exponentialRampToValueAtTime(660, ctx.currentTime + 0.14);

  // Sparkle: high triangle for shimmer.
  const shimmer = ctx.createOscillator();
  shimmer.type = "triangle";
  shimmer.frequency.setValueAtTime(1320, ctx.currentTime);
  shimmer.frequency.exponentialRampToValueAtTime(2640, ctx.currentTime + 0.14);

  const gain = ctx.createGain();
  const shimGain = ctx.createGain();
  envelope(ctx, gain, 0.18, 0.09);
  envelope(ctx, shimGain, 0.14, 0.025);

  // Low-pass filter softens the top-end so it feels warm, not harsh.
  const filter = ctx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.setValueAtTime(3200, ctx.currentTime);
  filter.Q.value = 0.6;

  osc.connect(gain).connect(filter).connect(ctx.destination);
  shimmer.connect(shimGain).connect(filter);

  osc.start();
  shimmer.start();
  osc.stop(ctx.currentTime + 0.2);
  shimmer.stop(ctx.currentTime + 0.16);
}

/** Crisp descending "click" — menu-close cue. */
export function playMenuCloseSound() {
  const ctx = getCtx();
  if (!ctx) return;

  const osc = ctx.createOscillator();
  osc.type = "sine";
  osc.frequency.setValueAtTime(520, ctx.currentTime);
  osc.frequency.exponentialRampToValueAtTime(180, ctx.currentTime + 0.09);

  const gain = ctx.createGain();
  envelope(ctx, gain, 0.11, 0.06);

  osc.connect(gain).connect(ctx.destination);
  osc.start();
  osc.stop(ctx.currentTime + 0.13);
}
